import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Paper, Grid, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { assignmentSubmit, getUserDetails } from "../../../../../Utils/apiService";

const AssignmentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const assignmentId = searchParams.get('assignment_id') || '2';
  const courseId = searchParams.get('course_id') || '1';
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [studentId, setStudentId] = useState<string>('0');
  const [studentLoading, setStudentLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchStudentDetails = async () => {
      try {
        const res: any = await getUserDetails();
        const id = res?.data?.id || res?.data?.user_id || res?.data?.userId;
        const localData = localStorage.getItem('userData');
        const parsedLocalData = localData ? JSON.parse(localData) : null;
        const fallbackId = parsedLocalData?.id || parsedLocalData?.userId || parsedLocalData?.user_id || '0';

        if (id) {
          setStudentId(String(id));
        } else if (fallbackId) {
          setStudentId(String(fallbackId));
        }
      } catch (error) {
        console.error('Failed to fetch student id from getUserDetails:', error);
        const localData = localStorage.getItem('userData');
        const parsedLocalData = localData ? JSON.parse(localData) : null;
        const fallbackId = parsedLocalData?.id || parsedLocalData?.userId || parsedLocalData?.user_id || '0';
        if (fallbackId) {
          setStudentId(String(fallbackId));
        }
      } finally {
        setStudentLoading(false);
      }
    };

    fetchStudentDetails();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadedFile(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  const handleSubmitAssignment = async () => {
    if (!uploadedFile) {
      alert('Please upload a file');
      return;
    }

    setIsLoading(true);
    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        const result = e.target?.result as string;
        const fileContent = (result?.split(',')[1] || result || '').toString(); // Ensure it's always a string
        console.log("file type", uploadedFile.type);
        console.log(uploadedFile.name);
console.log(uploadedFile.type);
console.log(fileContent.substring(0, 50));
        const payload = {
          method: 'post',
          id: Number(studentId),           
          instanceid: Number(assignmentId), 
          courseid: Number(courseId),
          filename: uploadedFile.name,
          filecontent: fileContent,       
          onlinetext: '',
        };

        try {
          const response = await assignmentSubmit(payload);
          console.log('Assignment submitted:', response);
          alert('Assignment submitted successfully!');
          setUploadedFile(null);
        } catch (error) {
          console.error('Error submitting assignment:', error);
          alert('Failed to submit assignment');
        } finally {
          setIsLoading(false);
        }
      };

      reader.readAsDataURL(uploadedFile); // Read as base64
    } catch (error) {
      console.error('Error preparing file:', error);
      alert('Error preparing file for submission');
      setIsLoading(false);
    }
  };

  return (
    <Grid item xs={12}>
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ px: { xs: 0, sm: 1 }, pb: { xs: 1, md: 2 } }}>

        {/* Instruction */}
        <Grid item xs={12} md={8}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: '12px',
              p: { xs: 2, sm: 2.5, md: 3 },
              minHeight: { xs: 'auto', md: 500 },
              height: '100%',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2} pb={2} sx={{ borderBottom: '1px solid #e0e0e0' }}>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: '#757575' }} />
              <Typography variant="body2" fontWeight={500}>Instruction</Typography>
            </Box>
            <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.9, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              In this assessment, you are required to research and document advanced React design patterns.
              <br />Your paper should cover:
              <br />1. Compound Components
              <br />2. Render Props vs. Custom Hooks
              <br />3. Higher Order Components (HOCs)
              <br />4. Performance optimization techniques (RequestAnimationFrame, Memoization)
              <br /><br />Requirements:
              <br />- Minimum 1500 words
              <br />- Proper citation in APA format
              <br />- Include code examples for each pattern
            </Typography>
          </Paper>
        </Grid>

        {/* Your Submission */}
        <Grid item xs={12} md={4}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: '12px',
              p: { xs: 2, sm: 2.5, md: 3 },
              minHeight: { xs: 'auto', md: 500 },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="text.primary">
              Your Submission
            </Typography>

            {/* Upload */}
            <Box
              onClick={() => !uploadedFile && document.getElementById('file-input')?.click()}
              sx={{
                border: uploadedFile ? '1.5px dashed #ccc' : '1.5px dashed #bdbdbd',
                borderRadius: '8px',
                minHeight: { xs: 120, md: 130 },
                px: { xs: 2, sm: 3 },
                py: { xs: 2.5, sm: 3 },
                cursor: uploadedFile ? 'not-allowed' : 'pointer',
                backgroundColor: uploadedFile ? '#f9f9f9' : '#fff',
                opacity: uploadedFile ? 0.6 : 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                transition: 'all 0.3s ease',
                '&:hover': uploadedFile ? {} : { borderColor: '#3e5193', backgroundColor: '#eef0fb' },
              }}
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf,.docx,.jpg,.zip"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={uploadedFile !== null}
              />
              <CloudUploadIcon sx={{ color: uploadedFile ? '#bdbdbd' : '#9e9e9e', fontSize: { xs: 24, sm: 28 } }} />
              <Typography variant="body2" textAlign="center">
                <span style={{ color: uploadedFile ? '#bdbdbd' : '#3e5193', fontWeight: 500 }}>Click to upload</span>
                {' '}or drag and drop
              </Typography>
              <Typography variant="caption" color={uploadedFile ? 'text.disabled' : 'text.secondary'} textAlign="center">
                pdf, .docx, .jpg, .zip (Max 10MB each)
              </Typography>
            </Box>

            {uploadedFile && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  gap: 1,
                }}
              >
                <Box display="flex" alignItems="center" gap={1} sx={{ overflow: 'hidden', minWidth: 0 }}>
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: '#3e5193', flexShrink: 0 }} />
                  <Typography variant="caption" noWrap sx={{ maxWidth: { xs: '180px', sm: '100%' } }}>{uploadedFile.name}</Typography>
                </Box>
                <CloseIcon
                  sx={{ fontSize: 14, color: '#9e9e9e', cursor: 'pointer', flexShrink: 0, ml: 1, '&:hover': { color: 'red' } }}
                  onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }}
                />
              </Box>
            )}

            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmitAssignment}
              disabled={isLoading || !uploadedFile || studentLoading || studentId === '0'}
              sx={{
                backgroundColor: '#a76f4d',
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
                padding: { xs: '11px', sm: '12px' },
                mt: 'auto',
                '&:hover': { backgroundColor: '#8a5a3a', boxShadow: 'none' },
                '&:disabled': { backgroundColor: '#ccc', cursor: 'not-allowed' },
              }}
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Submit Assignment'}
            </Button>
          </Paper>
        </Grid>

      </Grid>
    </Grid>
  );
};

export default AssignmentPage;
